import AuthView from '@/views/AuthView/AuthView.vue'
import HomeView from '@/modules/home/views/HomeView/HomeView.vue'
import RegistryView from '@/views/RegistryView/RegistryView.vue'
import { createRouter, createWebHistory } from 'vue-router'
import { useCompanyStore } from '@/store/company'
import { useCatalogStore } from '@/store/catalog'
import { useMainPageStore } from '@/store/mainPage'
import apiRoute from '@/router/api/index'
import adminRoute from '@/router/admin'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  strict: true,
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) {
      const el = document.querySelector(to.hash)
      if (el) {
        return new Promise(resolve => {
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
            resolve(false)
          }, 100)
        })
      }
    }
    if (savedPosition)
      return savedPosition
    return { top: 0 }
  },
  routes: [
    {
      path: '/:catchAll(.*)',
      name: 'not-found',
      component: () => import('@/views/NotFoundPage/NotFoundPage.vue'),
    },
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        layout: 'Main',
      },
      children: [
        {
          path: '/promo/:blockId/create',
          name: 'promoCreate',
          component: () => import('@/modules/home/views/PromoView/PromoView.vue'),
          meta: {
            layout: 'Main',
          },
        },
        {
          path: '/promo/:blockId/edit/:promoId',
          name: 'promoEdit',
          component: () => import('@/modules/home/views/PromoView/PromoView.vue'),
          meta: {
            layout: 'Main',
          },
          async beforeEnter(to, from, next) {
            const { blockId, promoId } = to.params

            const mainPageStore = useMainPageStore()
            const catalogStore = useCatalogStore()
            const { parentCategories, getParentCategories } = catalogStore
            if (!parentCategories) {
              await getParentCategories()
            }
            try {
              await mainPageStore.getBlockPromo({
                blockId: +blockId,
                pageId: +promoId,
              })
              next()
            } catch (error) {
              if (error)
                return next(from)
            }
          },
        },
      ],
    },
    {
      path: '/builder',
      name: 'builder',
      component: () => import('@/modules/builder/views/BuilderView/BuilderView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/shops',
      name: 'shops',
      component: () => import('@/modules/shops/views/ShopsPage/ShopsPage.vue'),
      meta: {
        layout: 'Main',
        withSearch: true,
      },
    },
    {
      path: '/shops/:id',
      name: 'shopsItem',
      component: () => import('@/modules/shops/views/ShopItemPage/ShopItemPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/shops/create',
      name: 'shopsItemCreate',
      component: () => import('@/modules/shops/views/ShopItemPage/ShopItemPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/orders',
      name: 'orders',
      component: () => import('@/modules/orders/views/OrdersPage/OrdersPage.vue'),
      meta: {
        layout: 'Main',
        withSearch: true,
      },
    },
    {
      path: '/orders/:id',
      name: 'ordersItem',
      component: () => import('@/modules/orders/views/OrderItemPage/OrderItemPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/orders/create',
      name: 'ordersItemCreate',
      component: () => import('@/modules/orders/views/OrderItemPage/OrderItemPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/clients',
      name: 'clients',
      component: () => import('@/modules/clients/views/ClientsPage/ClientsPage.vue'),
      meta: {
        layout: 'Main',
        withSearch: true,
      },
    },
    {
      path: '/clients/create',
      name: 'clientItemCreate',
      component: () => import('@/modules/clients/views/ClientItemView/ClientItemView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/clients/:id',
      name: 'clientItem',
      component: () => import('@/modules/clients/views/ClientItemView/ClientItemView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/catalog/categories/create',
      name: 'catalogCategoryCreate',
      component: () => import('@/modules/categories/views/CatalogCategory/CatalogCategory.vue'),
    },
    {
      path: '/catalog/categories/:id',
      name: 'catalogCategory',
      component: () => import('@/modules/categories/views/CatalogCategory/CatalogCategory.vue'),
    },
    {
      path: '/catalog/products/create',
      name: 'catalogProductCreate',
      component: () => import('@/modules/products/views/CatalogProduct/CatalogProduct.vue'),
    },
    {
      path: '/catalog/products/:id',
      name: 'catalogProduct',
      component: () => import('@/modules/products/views/CatalogProduct/CatalogProduct.vue'),
    },
    {
      path: '/catalog/products/:id/offers/create',
      name: 'catalogOfferCreate',
      component: () => import('@/modules/products/views/OfferProduct/OfferProduct.vue'),
    },
    {
      path: '/catalog/products/:id/offers/:offerId',
      name: 'catalogOffer',
      component: () => import('@/modules/products/views/OfferProduct/OfferProduct.vue'),
    },
    {
      path: '/appointments',
      name: 'appointments',
      meta: {
        layout: 'Main',
      },
      redirect: '/appointments/list',
      children: [
        {
          path: 'list',
          name: 'appointmentsList',
          component: () =>
            import('@/modules/appointments/views/AppointmentsList/AppointmentsList.vue'),
        },
        {
          path: 'calendar',
          name: 'appointmentsCalendar',
          component: () =>
            import('@/modules/appointments/views/AppointmentsSchedule/AppointmentsSchedule.vue'),
        },
        {
          path: 'create',
          name: 'appointmentsCreate',
          component: () =>
            import('@/modules/appointments/views/AppointmentsItem/AppointmentsItem.vue'),
        },
        {
          path: ':id',
          name: 'appointmentsEdit',
          component: () =>
            import('@/modules/appointments/views/AppointmentsItem/AppointmentsItem.vue'),
        },
      ],
    },

    {
      path: '/specialists',
      name: 'specialists',
      component: () => import('@/modules/specialists/views/SpecialistsList/SpecialistsList.vue'),
      meta: {
        layout: 'Main',
        withSearch: true,
      },
    },
    {
      path: '/specialists/create',
      name: 'specialistItemCreate',
      component: () => import('@/modules/specialists/views/SpecialistItem/SpecialistItem.vue'),
    },
    {
      path: '/specialists/:id',
      name: 'specialistItem',
      component: () => import('@/modules/specialists/views/SpecialistItem/SpecialistItem.vue'),
    },
    {
      path: '/specialists/:id/certificates/create',
      name: 'certificateItemCreate',
      component: () => import('@/modules/specialists/views/CertificatesItem/CertificatesItem.vue'),
    },
    {
      path: '/specialists/:id/certificates/:certificateId',
      name: 'certificateItem',
      component: () => import('@/modules/specialists/views/CertificatesItem/CertificatesItem.vue'),
    },
    {
      path: '/catalog',
      component: () => import('@/views/CatalogPage/CatalogPage.vue'),
      meta: {
        layout: 'Main',
      },
      children: [
        {
          path: 'categories',
          name: 'catalogCategories',
          meta: { withSearch: true },
          component: () =>
            import('@/modules/categories/views/CatalogCategories/CatalogCategories.vue'),
        },
        {
          path: 'kits',
          name: 'catalogKits',
          component: () => import('@/modules/kits/views/CatalogKits/CatalogKits.vue'),
          meta: {
            layout: 'Main',
            withSearch: true,
          },
        },
        {
          path: 'products',
          name: 'catalogProducts',
          component: () => import('@/modules/products/views/CatalogProducts/CatalogProducts.vue'),
          meta: {
            layout: 'Main',
            withSearch: true,
          },
        },
      ],
    },
    {
      path: '/services',
      name: 'services',
      meta: {
        layout: 'Main',
      },
      redirect: '/services/categories',
      children: [
        {
          path: 'categories',
          name: 'serviceCategories',
          meta: {
            withSearch: true,
          },
          component: () =>
            import('@/modules/services/views/ServiceCategories/ServiceCategories.vue'),
        },
        {
          path: 'search',
          name: 'servicesSearch',
          meta: {
            withSearch: true,
          },
          component: () => import('@/modules/services/views/ServicesSearch/ServicesSearch.vue'),
        },
        {
          path: 'create',
          name: 'serviceCreate',
          component: () => import('@/modules/services/views/ServiceItem/ServiceItem.vue'),
        },
        {
          path: ':id',
          name: 'serviceEdit',
          component: () => import('@/modules/services/views/ServiceItem/ServiceItem.vue'),
        },
      ],
    },
    {
      path: '/services/categories/create',
      name: 'serviceCategoryCreate',
      component: () => import('@/modules/services/views/ServiceCategory/ServiceCategory.vue'),
    },
    {
      path: '/services/categories/:id',
      name: 'serviceCategory',
      component: () => import('@/modules/services/views/ServiceCategory/ServiceCategory.vue'),
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('@/views/ProfileView/ProfileView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/subscription/success',
      name: 'subscriptionSuccess',
      component: () => import('@/views/SubscriptionReturnView/SubscriptionReturnView.vue'),
      props: { outcome: 'success' },
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/subscription/fail',
      name: 'subscriptionFail',
      component: () => import('@/views/SubscriptionReturnView/SubscriptionReturnView.vue'),
      props: { outcome: 'fail' },
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/company',
      name: 'company',
      component: () => import('@/modules/company/views/CompanyView/CompanyView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/company/:id',
      name: 'menuSection',
      component: () =>
        import('@/modules/company/views/CompanyMenuItemView/CompanyMenuItemView.vue'),
      meta: {
        layout: 'Main',
      },
      async beforeEnter(to, from, next) {
        const companyStore = useCompanyStore()
        const { id } = to.params

        try {
          await companyStore.getMenuSection({ id: +id })
          return next()
        } catch {
          return next({ name: 'home' })
        }
      },
    },
    {
      path: '/news',
      name: 'news',
      component: () => import('@/modules/news/views/NewsView/NewsView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/finance',
      name: 'finance',
      component: () => import('@/views/FinanceView/FinanceView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/settings',
      component: () => import('@/modules/settings/views/SettingsView/SettingsView.vue'),
      meta: {
        layout: 'Main',
      },
      children: [
        {
          path: 'main',
          name: 'mainSettings',
          component: () =>
            import('@/modules/settings/components/main/MainSettings/MainSettings.vue'),
          meta: {
            layout: 'Main',
            tab: 'main',
          },
        },
        {
          path: 'store',
          name: 'storeSettings',
          component: () =>
            import('@/modules/settings/components/store/StoreSettings/StoreSettings.vue'),
          meta: {
            layout: 'Main',
            tab: 'store',
          },
        },
        {
          path: 'schedule',
          name: 'scheduleSettings',
          component: () =>
            import('@/modules/settings/components/ScheduleComponents/ScheduleComponents.vue'),
          meta: {
            layout: 'Main',
            tab: 'schedule',
          },
        },
        {
          path: 'services',
          name: 'servicesSettings',
          component: () =>
            import('@/modules/settings/components/service/ServiceSettings/ServiceSettings.vue'),
          meta: {
            layout: 'Main',
            tab: 'services',
          },
        },
        {
          path: 'booking',
          name: 'bookingIntegrationsSettings',
          component: () =>
            import('@/modules/settings/components/booking/BookingIntegrations/BookingIntegrations.vue'),
          meta: {
            layout: 'Main',
            tab: 'booking',
          },
        },
        {
          path: 'pay-delivery',
          name: 'payDeliverySettings',
          component: () =>
            import('@/modules/settings/components/delivery/PayDeliverySettings/PayDeliverySettings.vue'),
          meta: {
            layout: 'Main',
            tab: 'pay-delivery',
          },
        },
        {
          path: 'bottom-navbar',
          name: 'bottomNavBarSettings',
          component: () =>
            import('@/modules/settings/components/BottomNavBarSettings/BottomNavBarSettings.vue'),
          meta: {
            layout: 'Main',
            tab: 'bottom-navbar',
          },
        },
      ],
    },
    {
      path: '/integrations',
      name: 'integrations',
      component: () =>
        import('@/modules/integrations/views/IntergrationsView/IntergrationsView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/integrations/pos/evotor',
      name: 'posEvotor',
      component: () =>
        import('@/modules/integrations/views/PosEvotorPage/PosEvotorPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/integrations/migration/yclients',
      name: 'yclientsMigration',
      component: () =>
        import('@/modules/integrations/views/YclientsMigrationPage/YclientsMigrationPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/integrations/:connector',
      name: 'integration',
      component: () =>
        import('@/modules/integrations/views/IntegrationPage/IntegrationPage.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/login',
      meta: {
        layout: 'Auth',
      },
      children: [
        {
          path: '',
          name: 'login',
          component: AuthView,
        },
        {
          path: 'recovery',
          name: 'recovery',
          component: AuthView,
        },
        {
          path: 'update',
          name: 'update',
          component: () => import('@/views/ResetPasswordView/ResetPasswordView.vue'),
        },
      ],
    },
    {
      path: '/registry',
      name: 'registry',
      component: RegistryView,
      meta: {
        layout: 'Auth',
      },
    },
    {
      path: '/start',
      name: 'startScreen',
      component: () => import('@/modules/start/views/StartView/StartView.vue'),
      meta: {
        layout: 'StartScreen',
      },
    },
    {
      path: '/download',
      name: 'download',
      component: () => import('@/modules/start/views/DownloadView/DownloadView.vue'),
      meta: {
        layout: 'StartScreen',
      },
    },
    {
      path: '/push',
      name: 'push',
      component: () => import('@/modules/push/views/PushListView/PushListView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    {
      path: '/push/create',
      name: 'pushCreate',
      component: () => import('@/modules/push/views/PushItemView/PushItemView.vue'),
      meta: {
        layout: 'Main',
      },
    },
    ...apiRoute,
    ...adminRoute,
  ],
})

export default router
